import { useCallback, useRef } from 'react';

export interface WheelScrollMetrics {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

// Approx line height for `deltaMode === 1` (Firefox line-based wheel deltas).
const LINE_HEIGHT_PX = 16;

/**
 * Decide how far (in px) to manually scroll a container for a wheel event, or
 * `null` to let the event pass through untouched.
 *
 * Returns a delta ONLY when the container can still scroll in the wheel's
 * direction — at a boundary (or with nothing to scroll) it returns `null` so
 * normal scroll-chaining is preserved. Normalises `deltaMode` (lines/pages) to
 * pixels so trackpad and line-based wheels behave the same.
 */
export const wheelScrollDelta = (
  event: { deltaY: number; deltaMode: number },
  { scrollTop, scrollHeight, clientHeight }: WheelScrollMetrics
): number | null => {
  const maxScroll = scrollHeight - clientHeight;
  if (maxScroll <= 0) return null;

  const px =
    event.deltaMode === 1
      ? event.deltaY * LINE_HEIGHT_PX
      : event.deltaMode === 2
        ? event.deltaY * clientHeight
        : event.deltaY;

  if (px > 0 && scrollTop < maxScroll) return px;
  if (px < 0 && scrollTop > 0) return px;
  return null;
};

/**
 * Keeps a body-portaled scroll container wheel-scrollable even while a
 * `react-remove-scroll` lock (Chakra `blockScrollOnMount` on any open
 * drawer/modal) is preventing native wheel scrolling outside its whitelist.
 *
 * The lock's document-level handler `preventDefault()`s the native scroll but
 * doesn't stop propagation, so a non-passive `wheel` listener on the container
 * still fires and can scroll it manually. Only kicks in when there's room to
 * scroll, so it's a no-op at boundaries and when no lock is active.
 *
 * Needed because the automation underway popup sits ABOVE the candidate
 * surfaces (see src/constants/zIndex.ts), so it stays interactive while those
 * drawers are open.
 *
 * Returns a CALLBACK ref (not a ref object) on purpose: the popup renders inside
 * a Chakra `<Portal>`, which defers mounting its children past the host's first
 * commit — a `useEffect`/`useRef` pair would run once with a null node and never
 * re-attach. A callback ref fires whenever the node actually mounts/unmounts.
 */
export const useWheelScrollThroughLock = <
  T extends HTMLElement = HTMLDivElement,
>() => {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((node: T | null) => {
    // Detach from any previous node first.
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      const delta = wheelScrollDelta(event, {
        scrollTop: node.scrollTop,
        scrollHeight: node.scrollHeight,
        clientHeight: node.clientHeight,
      });
      if (delta === null) return;
      node.scrollTop += delta;
      event.preventDefault();
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    cleanupRef.current = () => node.removeEventListener('wheel', onWheel);
  }, []);
};
